import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import * as z from 'zod';

import axios from '../localAxios';
import { generateQuestionsByAreasOfImprovementURL } from './urls';

interface GenerateQuestionByAreasOfImprovementParams {
  activityInstanceId: number;
  insightIndexes: Array<number>;
  questionCount: number;
  generateExplanations: boolean;
}

export async function generateQuestionByAreasOfImprovement({
  activityInstanceId,
  insightIndexes,
  questionCount,
  generateExplanations,
}: GenerateQuestionByAreasOfImprovementParams) {
  const url = generateQuestionsByAreasOfImprovementURL(activityInstanceId);

  const response = await axios.post(url.href, {
    insightIndexes,
    questionCount,
    generateExplanations,
  });

  const { data, error } = generateQuestionsResponseSchema.safeParse(
    response.data
  );

  if (error) {
    throw new Error(error.message);
  }
  return data;
}

const AnswerSchema = z.object({
  order: z.number(),
  text: z.string(),
  isCorrect: z.boolean(),
});

const MultipleChoiceQuestionSchema = z.object({
  questionType: z.literal(QuestionTypes.MultipleChoice),
  questionText: z.string(),
  explanation: z.string().optional(),
  answers: z.array(AnswerSchema),
});

const TrueFalseQuestionSchema = z.object({
  questionType: z.literal(QuestionTypes.TrueFalse),
  questionText: z.string(),
  explanation: z.string().optional(),
  answers: z.array(AnswerSchema),
});
const ShortAnswerQuestionSchema = z.object({
  questionType: z.literal(QuestionTypes.FreeResponse),
  questionText: z.string(),
  explanation: z.string().optional(),
  answers: z.array(AnswerSchema),
});

const UsageSchema = z.object({
  completionTokens: z.number(),
  promptTokens: z.number(),
  totalTokens: z.number(),
});

const QuestionSchema = z.discriminatedUnion('questionType', [
  MultipleChoiceQuestionSchema,
  TrueFalseQuestionSchema,
  ShortAnswerQuestionSchema,
]);

export const generateQuestionsResponseSchema = z.object({
  activityInstanceId: z.number(),
  insightId: z.string(),
  questions: z.array(QuestionSchema),
  elapsedTime: z.number(),
  version: z.string(),
  promptVersion: z.string(),
  usage: UsageSchema,
});

export type GenerateQuestionsResponse = z.infer<
  typeof generateQuestionsResponseSchema
>;

export type Question = z.infer<typeof QuestionSchema>;
export type Answer = z.infer<typeof AnswerSchema>;
export type Usage = z.infer<typeof UsageSchema>;
export type MultipleChoiceQuestion = z.infer<
  typeof MultipleChoiceQuestionSchema
>;
export type TrueFalseQuestion = z.infer<typeof TrueFalseQuestionSchema>;
export type ShortAnswerQuestion = z.infer<typeof ShortAnswerQuestionSchema>;
